import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import KpiCard from '../ui/KpiCard';
import { RestaurantMenu, CheckCircle, Storefront, Money } from '../ui/Icons';

/**
 * At-a-glance summary metrics for the Menu Items tab — four KpiCards
 * (Total Items / Available Now / Categories / Top Sellers). Presentational:
 * counts are derived from the same `menuItems` + `sectionOrder` the page already
 * holds, so the row needs no extra fetch. Deltas are omitted (no trend data).
 */
const isAvailable = (item) => item.is_available === 1 || item.is_available === true;

const MenuStatsRow = ({ menuItems = [], sectionOrder = [] }) => {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    const total = menuItems.length;
    const available = menuItems.filter(isAvailable).length;
    const topSellers = menuItems.filter((i) => i.is_popular).length;
    // Fall back to distinct item sections when the order list is empty.
    const categories = sectionOrder.length
      ? sectionOrder.length
      : new Set(menuItems.map((i) => i.section || 'Other')).size;
    return { total, available, topSellers, categories };
  }, [menuItems, sectionOrder]);

  const cards = [
    {
      key: 'total',
      icon: <RestaurantMenu size={22} />,
      iconTone: 'primary',
      label: t('menuV2.stats.totalItems', 'Total Items'),
      value: stats.total,
    },
    {
      key: 'available',
      icon: <CheckCircle size={22} />,
      iconTone: 'success',
      label: t('menuV2.stats.available', 'Available Now'),
      value: stats.available,
    },
    {
      key: 'categories',
      icon: <Storefront size={22} />,
      iconTone: 'info',
      label: t('menuV2.stats.categories', 'Categories'),
      value: stats.categories,
    },
    {
      key: 'topSellers',
      icon: <Money size={22} />,
      iconTone: 'secondary',
      label: t('menuV2.stats.topSellers', 'Top Sellers'),
      value: stats.topSellers,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((c) => (
        <KpiCard
          key={c.key}
          icon={c.icon}
          iconTone={c.iconTone}
          label={c.label}
          value={c.value}
        />
      ))}
    </Box>
  );
};

export default MenuStatsRow;
